import { cn } from '@/lib/utils'

import { Skeleton, useProductSliderVariants } from '../../../Theme'
import { ProductCardSkeleton, SectionTitle } from '../../../UiBlocks'

import type { ProductSliderSkeletonProps } from '../../types'

/**
 * ProductSliderSkeleton - Loading state for product slider
 *
 * Features:
 * - Uses ProductCardSkeleton for consistent card loading states
 * - Responsive grid: 1 mobile, 2 tablet, 3 desktop (matches ProductSlider)
 * - Consistent dimensions with actual slider
 * - Accessible: uses aria-busy for screen readers
 * - CVA variants via UiProvider
 *
 * @example
 * <ProductSliderSkeleton />
 */
export function ProductSliderSkeleton({ className, count = 3 }: ProductSliderSkeletonProps) {
  const {
    productSliderSectionVariants,
    productSliderWrapperVariants,
    productSliderSkeletonGridVariants,
  } = useProductSliderVariants()

  return (
    <section
      className={cn(productSliderSectionVariants(), className)}
      aria-busy="true"
      aria-label="Loading products"
    >
      {/* Title Skeleton */}
      <SectionTitle size="lg">
        <Skeleton className="h-10 w-48 mx-auto" />
      </SectionTitle>

      {/* Products Grid Skeleton */}
      <div className={productSliderWrapperVariants()}>
        <div className={productSliderSkeletonGridVariants()}>
          {Array.from({ length: count }, (_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </section>
  )
}
